import { type ErrorEvent, type EventHint } from "@sentry/react";
import { isAxiosError } from "axios";

const UUID_PATH_SEGMENT =
  /\/[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}(?=\/|$)/gi;

const readStringProperty = (value: unknown, property: string) => {
  if (typeof value !== "object" || value === null) {
    return undefined;
  }

  const propertyValue = Reflect.get(value, property);
  return typeof propertyValue === "string" && propertyValue.trim()
    ? propertyValue
    : undefined;
};

const getAxiosResponseMessage = (data: unknown) => {
  const error =
    typeof data === "object" && data !== null
      ? Reflect.get(data, "error")
      : undefined;
  const meta =
    typeof data === "object" && data !== null
      ? Reflect.get(data, "meta")
      : undefined;

  return (
    readStringProperty(error, "logMsg") ??
    readStringProperty(meta, "userMsg") ??
    readStringProperty(data, "message") ??
    readStringProperty(data, "detail")
  );
};

const getRequestPath = (url?: string, baseURL?: string) => {
  if (!url) {
    return "unknown endpoint";
  }

  try {
    return new URL(url, baseURL ?? window.location.origin).pathname.replace(
      UUID_PATH_SEGMENT,
      "/:id",
    );
  } catch {
    return url.split("?")[0].replace(UUID_PATH_SEGMENT, "/:id");
  }
};

const hasHeaderGetter = (
  value: object,
): value is { get: (name: string) => unknown } => {
  const candidate = value as { get?: unknown };
  return typeof candidate.get === "function";
};

const getResponseHeader = (headers: unknown, name: string) => {
  if (typeof headers !== "object" || headers === null) {
    return undefined;
  }

  if (hasHeaderGetter(headers)) {
    const value = headers.get(name);
    if (typeof value === "string" && value.trim()) {
      return value;
    }
  }

  return readStringProperty(headers, name);
};

export const enrichAxiosErrorEvent = (event: ErrorEvent, hint: EventHint) => {
  const exception = hint.originalException;
  if (!isAxiosError(exception)) {
    return event;
  }

  const method = exception.config?.method?.toUpperCase() ?? "HTTP";
  const path = getRequestPath(exception.config?.url, exception.config?.baseURL);
  const status = exception.response?.status;
  const responseMessage = getAxiosResponseMessage(exception.response?.data);
  const requestId =
    getResponseHeader(exception.response?.headers, "x-request-id") ??
    getResponseHeader(exception.response?.headers, "x-amzn-requestid");
  const statusDescription =
    status !== undefined ? ` returned ${status}` : " failed";
  const eventTitle = `${method} ${path}${statusDescription}${
    responseMessage ? `: ${responseMessage}` : ""
  }`;

  const exceptionValue = event.exception?.values?.[0];
  if (exceptionValue) {
    exceptionValue.value = eventTitle;
  }

  event.tags = {
    ...event.tags,
    "api.endpoint": path,
    "http.request.method": method,
    ...(status !== undefined
      ? { "http.response.status_code": String(status) }
      : {}),
  };
  event.contexts = {
    ...event.contexts,
    api_request: {
      method,
      path,
      ...(status !== undefined ? { status } : {}),
      ...(exception.code ? { axiosCode: exception.code } : {}),
      ...(responseMessage ? { responseMessage } : {}),
      ...(requestId ? { requestId } : {}),
    },
  };
  event.fingerprint = [
    "{{ default }}",
    "axios",
    method,
    path,
    status !== undefined ? String(status) : (exception.code ?? "network-error"),
  ];

  return event;
};
